import { Divider, Stack, Typography } from '@mui/material';
import {
  BackupPolicyType,
  ScheduleMode,
  ScheduleType,
  type BackupSchedule,
} from '@repo/api-gw-sdk';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { BackupPolicyScheduleList } from './backupPolicyScheduleList';
import { CustomPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { ScheduleModeSelector } from './scheduleModeSelector';

import { MIN_WINDOW_HOURS } from '../controls/hoursSelector';
import {
  dailyFrequency,
  getFrequencyByWindow,
  thirtyMinutesFrequency,
} from '../frequency';

const STANDARD_TIMES_PER_DAY = 4;
const HIGH_FREQUENCY_TIMES_PER_DAY = 48;
const PITR_TIMES_PER_DAY = 96;

export const BackupPolicySettings = (
  props: StepProps<BackupPolicyEditState>
) => {
  const [useUtc, setUseUtc] = useState(
    !!props.wizardState.schedules?.[0]?.useUtc
  );

  const addSchedule = (scheduleType: ScheduleType) => {
    let vaultId = '';
    const newState = {
      ...props.wizardState,
    };

    if (scheduleType === ScheduleType.SCHEDULE_TYPE_PITR) {
      const standardSchedules = props.wizardState.schedules?.filter(
        (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
      );
      vaultId =
        standardSchedules?.find((standardSchedule) => {
          if (!standardSchedule.vaultId) {
            return false;
          }
          const frequency = getFrequencyByWindow(standardSchedule.windows);
          return (
            frequency?.timesPerDay ||
            frequency?.windows === dailyFrequency.windows
          );
        })?.vaultId || '';
    }

    const defaultFrequency =
      scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
        ? thirtyMinutesFrequency
        : dailyFrequency;

    newState.schedules = [
      ...(newState.schedules ?? []),
      {
        id: uuid(),
        duration: MIN_WINDOW_HOURS * 60,
        backupRetention: defaultFrequency.defaultRetentionInDays,
        vaultId,
        windows: defaultFrequency.windows,
        scheduleType,
      },
    ];

    props.setWizardState(newState);
  };

  const updateSchedule = (schedule: BackupSchedule) => {
    const schedules =
      props.wizardState.schedules?.map((s) =>
        s.id === schedule.id ? schedule : s
      ) ?? [];

    props.setWizardState({ ...props.wizardState, schedules });
  };

  const removeSchedule = (schedule: BackupSchedule) => {
    if (props.wizardState.schedules?.length === 1) return;
    props.setWizardState({
      ...props.wizardState,
      schedules: props.wizardState.schedules?.filter(
        (s) => s.id !== schedule.id
      ),
    });
  };

  const duplicateSchedule = (schedule: BackupSchedule) => {
    props.setWizardState({
      ...props.wizardState,
      schedules: [
        ...(props.wizardState.schedules ?? []),
        { ...schedule, id: uuid() },
      ],
    });
  };

  const timesPerDay =
    props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
      ? PITR_TIMES_PER_DAY
      : props.wizardState.policyType ===
          BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
        ? HIGH_FREQUENCY_TIMES_PER_DAY
        : STANDARD_TIMES_PER_DAY;

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
        stepperLabels={props.stepperLabels}
        stepperIndex={2}
        onBackClick={props.back}
        nextButtonDisabledTooltip={
          props.wizardState.policyType ===
          BackupPolicyType.BACKUP_POLICY_TYPE_PITR
            ? 'Make sure all fields are filled in, and that PiTR schedules meet the necessary requirements'
            : 'Please complete all required fields'
        }
        canGoNext={() => {
          if (!props.wizardState.schedules?.length) {
            return false;
          }

          for (const schedule of props.wizardState.schedules) {
            if (!schedule.vaultId) {
              return false;
            }
          }

          if (
            props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_PITR
          ) {
            const pitrSchedules = props.wizardState.schedules.filter(
              (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
            );

            // Each vault can only be used once in PiTR
            if (
              new Set(pitrSchedules.map((x) => x.vaultId)).size !==
              pitrSchedules.length
            ) {
              return false;
            }

            const standardSchedules = props.wizardState.schedules.filter(
              (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
            );

            const allPitrSchedulesHaveRelatedStandard = pitrSchedules.every(
              (pitrSchedule) => {
                return standardSchedules.some((standardSchedule) => {
                  const frequency = getFrequencyByWindow(
                    standardSchedule.windows
                  );
                  return (
                    standardSchedule.vaultId === pitrSchedule.vaultId &&
                    standardSchedule.backupRetention >=
                      pitrSchedule.backupRetention &&
                    (frequency?.timesPerDay ||
                      frequency?.windows === dailyFrequency.windows)
                  );
                });
              }
            );

            return allPitrSchedulesHaveRelatedStandard;
          }

          return true;
        }}
        nextButtonText={props.wizardState.id ? 'Save Changes' : 'Create'}
        onNextClick={() => {
          props.wizardState.schedules?.forEach((s) => {
            s.useUtc =
              props.wizardState.mode === ScheduleMode.SCHEDULE_MODE_ADVANCED
                ? useUtc
                : false;
          });

          return props.finish();
        }}
      >
        <CustomPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
          vaultManagedType={props.wizardState.vaultManagedType}
        />
        <Stack className='px-[min(100px,5vw)] pt-[min(60px,3vw)]'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: '16px' }}
          >
            <Typography variant='body1'>Backup schedules</Typography>
            <ScheduleModeSelector
              wizardState={props.wizardState}
              setWizardState={props.setWizardState}
              useUtc={useUtc}
              setUseUtc={setUseUtc}
              timesPerDay={timesPerDay}
            />
          </Stack>
          <Stack divider={<Divider />} gap='40px'>
            <BackupPolicyScheduleList
              schedules={
                props.wizardState.schedules?.filter(
                  (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
                ) ?? []
              }
              deleteAllowed={
                (props.wizardState.schedules?.filter(
                  (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
                ).length ?? 0) > 1
              }
              onUpdate={updateSchedule}
              onDuplicate={duplicateSchedule}
              onDelete={removeSchedule}
              mode={props.wizardState.mode}
              onAdd={() => addSchedule(ScheduleType.SCHEDULE_TYPE_STANDARD)}
              addButtonText='Add Backup Schedule'
              maxTimesPerDay={
                props.wizardState.policyType ===
                BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
                  ? HIGH_FREQUENCY_TIMES_PER_DAY
                  : STANDARD_TIMES_PER_DAY
              }
              vaultManagedType={props.wizardState.vaultManagedType}
            />
            {props.wizardState.policyType ===
              BackupPolicyType.BACKUP_POLICY_TYPE_PITR && (
              <BackupPolicyScheduleList
                title='Point-in-time recovery (PiTR) schedules'
                subtitle='Requires a daily or more frequent backup schedule above, that uses the same vault and has a longer retention period. Each vault can only be used once in this section.'
                deleteAllowed={true}
                schedules={
                  props.wizardState.schedules?.filter(
                    (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
                  ) ?? []
                }
                onUpdate={updateSchedule}
                onDuplicate={duplicateSchedule}
                onDelete={removeSchedule}
                mode={props.wizardState.mode}
                onAdd={() => addSchedule(ScheduleType.SCHEDULE_TYPE_PITR)}
                addButtonText='Add PiTR Schedule'
                maxTimesPerDay={PITR_TIMES_PER_DAY}
                vaultManagedType={props.wizardState.vaultManagedType}
              />
            )}
          </Stack>
        </Stack>
      </StepContainer>
    </>
  );
};
